'use client';

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Swiper as SwiperInstance } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { BookOpen, Play, Search } from 'lucide-react';
import { ObraCoverImage } from '@/components/obra/ObraCoverImage';
import { getObraImageUrl } from '@/utils/image-utils';
import { generateWorkLink } from '@/utils/utils';
import { api } from '@/utils/api';
import { OBRAS_GENEROS, accentHexForFormato, isHentaiFormato, rgbaFromHex } from '@/constants/obras-generos';
import ObrasHomeSkeleton from '@/components/ui/ObrasHomeSkeleton';
import { formatoPillClassName } from '@/components/obra/formatoPillStyles';

const GENEROS = OBRAS_GENEROS;

/** «O Segundo Eunuco Recupera Sua Masculinidade» — arte wide só no cartão central (desktop). */
const OBRA_EUNUCO_DESKTOP_BANNER_ID = 43;
const OBRA_EUNUCO_DESKTOP_BANNER_URL = 'https://i.ibb.co/hRpVMFxh/image.png';

/** «A Marca Cinza» — mesma arte em todos os estados; no centro só alarga o cartão (desktop). */
const OBRA_MARCA_CINZA_DESKTOP_ID = 17700;
const OBRA_MARCA_CINZA_DESKTOP_URL = 'https://i.ibb.co/Ld8MHSYd/image.png';

interface ObraItem {
    id: number;
    nome?: string;
    imagem?: string;
    banner?: string;
    total_capitulos?: number;
}

interface ObrasHomeProps {
    formatoId?: number;
    /** Troca de formato na home — mesmos pills que o app. */
    onFormatoChange?: (formato: number) => void;
}

function ensureList(res: unknown): unknown[] {
    if (Array.isArray(res)) return res;
    if (res && typeof res === 'object') {
        const obj = res as { data?: unknown; items?: unknown };
        if (Array.isArray(obj.data)) return obj.data;
        if (Array.isArray(obj.items)) return obj.items;
    }
    return [];
}

type AppNavigate = ReturnType<typeof useNavigate>;

/** Desktop: pills de género numa única linha (scroll horizontal se estreito). */
function HomeDesktopFormatoStrip({
    formatoId,
    onFormatoChange,
}: {
    formatoId: number;
    onFormatoChange?: (formato: number) => void;
}) {
    if (!onFormatoChange) return null;
    return (
        <div className="mb-5 hidden w-full max-w-[1400px] md:mx-auto md:block md:px-6">
            <div className="rounded-2xl border border-white/10 bg-black/35 px-4 py-3 backdrop-blur-[1px]">
                <div className="flex flex-nowrap items-center justify-center gap-2.5 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] md:gap-3 [&::-webkit-scrollbar]:hidden">
                    {GENEROS.map((genero) => {
                        const ativo = genero.formato === formatoId;
                        return (
                            <button
                                key={genero.id}
                                type="button"
                                onClick={() => onFormatoChange(genero.formato)}
                                className={formatoPillClassName(ativo)}
                                style={
                                    ativo
                                        ? {
                                              backgroundColor: `${genero.color}20`,
                                              color: genero.color,
                                              borderColor: genero.color,
                                          }
                                        : {}
                                }
                            >
                                {genero.label}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

/** Mobile: barra «Buscar obra» + pills de género (estilo med-app). */
function ExploreSearchAndFormatos({
    formatoId,
    onFormatoChange,
    navigate,
    showCarousel,
}: {
    formatoId: number;
    onFormatoChange?: (formato: number) => void;
    navigate: AppNavigate;
    showCarousel: boolean;
}) {
    return (
        <div className={showCarousel ? '-mt-[100px]' : ''}>
            <div className={`relative z-20 isolate px-4 pb-3 ${showCarousel ? 'pt-1' : 'pt-4'}`}>
                <button
                    type="button"
                    onClick={() => navigate(`/pesquisar?formato=${formatoId}`)}
                    className="w-full flex cursor-pointer items-center gap-3 rounded-2xl border px-5 py-3.5 text-left backdrop-blur-sm transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out border-white/10 bg-neutral-900/55 text-white/55 hover:-translate-y-px hover:border-white/20 hover:bg-neutral-900/85 hover:text-white/90 hover:shadow-[0_8px_28px_rgba(0,0,0,0.45)] active:scale-[0.99] motion-reduce:hover:translate-y-0 motion-reduce:hover:shadow-none"
                >
                    <Search size={20} className="shrink-0 text-white/40" />
                    <span className="text-sm font-medium">Buscar obra</span>
                </button>
            </div>

            {onFormatoChange && (
                <div className="relative z-10 px-4 pt-0 pb-2">
                    <div className="rounded-2xl border px-4 py-3 backdrop-blur-[1px] border-white/10 bg-black/35">
                        <div className="flex flex-col items-center gap-2.5">
                            <div className="flex flex-wrap justify-center gap-2">
                                {GENEROS.slice(0, 4).map((genero) => {
                                    const ativo = genero.formato === formatoId;
                                    return (
                                        <button
                                            key={genero.id}
                                            type="button"
                                            onClick={() => onFormatoChange(genero.formato)}
                                            className={formatoPillClassName(ativo)}
                                            style={
                                                ativo
                                                    ? {
                                                          backgroundColor: `${genero.color}20`,
                                                          color: genero.color,
                                                          borderColor: genero.color,
                                                      }
                                                    : {}
                                            }
                                        >
                                            {genero.label}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="flex flex-wrap justify-center gap-2">
                                {GENEROS.slice(4, 8).map((genero) => {
                                    const ativo = genero.formato === formatoId;
                                    return (
                                        <button
                                            key={genero.id}
                                            type="button"
                                            onClick={() => onFormatoChange(genero.formato)}
                                            className={formatoPillClassName(ativo)}
                                            style={
                                                ativo
                                                    ? {
                                                          backgroundColor: `${genero.color}20`,
                                                          color: genero.color,
                                                          borderColor: genero.color,
                                                      }
                                                    : {}
                                            }
                                        >
                                            {genero.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function ObrasHome({ formatoId = 5, onFormatoChange }: ObrasHomeProps) {
    const navigate = useNavigate();
    const [obras, setObras] = useState<ObraItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [swiperActiveIndex, setSwiperActiveIndex] = useState<number | null>(null);
    const [eunucoRevealBanner, setEunucoRevealBanner] = useState(false);
    const mobileSwiperRef = useRef<SwiperInstance | null>(null);
    const desktopSwiperRef = useRef<SwiperInstance | null>(null);

    const accent = accentHexForFormato(formatoId);
    const hentaiBanner = isHentaiFormato(formatoId);

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            setLoading(true);
            try {
                const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
                const limite = isMobile ? 8 : 15;
                const { data: raw } = await api.get(`obras/home?formato=${formatoId}&limit=${limite}`);
                const list = ensureList(raw) as ObraItem[];
                if (!cancelled) setObras(list);
            } catch {
                if (!cancelled) setObras([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [formatoId]);

    const list = Array.isArray(obras) ? obras : [];
    const desktopCenterIndex = Math.min(Math.floor(list.length / 2), Math.max(0, list.length - 1));
    const activeIndex = swiperActiveIndex ?? desktopCenterIndex;
    const desktopMarcaCinzaExpanded = list[activeIndex]?.id === OBRA_MARCA_CINZA_DESKTOP_ID;

    useEffect(() => {
        setSwiperActiveIndex(null);
    }, [formatoId]);

    useEffect(() => {
        const activeId = list[activeIndex]?.id;
        if (activeId !== OBRA_EUNUCO_DESKTOP_BANNER_ID) {
            setEunucoRevealBanner(false);
            return;
        }
        setEunucoRevealBanner(false);
        const t = window.setTimeout(() => setEunucoRevealBanner(true), 3000);
        return () => window.clearTimeout(t);
    }, [activeIndex, list]);

    /** Recalcula centro do Swiper quando a largura muda (obra 43 expandida ou «A Marca Cinza» no centro). */
    useEffect(() => {
        const s = desktopSwiperRef.current;
        if (!s) return;
        const loop = list.length >= 3;
        const snap = () => {
            s.update();
            const ri = s.realIndex;
            if (typeof s.slideToLoop === 'function' && loop) {
                s.slideToLoop(ri, 0);
            } else {
                s.slideTo(ri, 0);
            }
        };
        const id = requestAnimationFrame(snap);
        const t1 = window.setTimeout(snap, 60);
        const t2 = window.setTimeout(snap, 1100);
        return () => {
            cancelAnimationFrame(id);
            window.clearTimeout(t1);
            window.clearTimeout(t2);
        };
    }, [eunucoRevealBanner, desktopMarcaCinzaExpanded]);

    const isCoarsePointer = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
    const shouldAutoplay = list.length > 1 && !isCoarsePointer;

    useEffect(() => {
        if (list.length === 0 || !shouldAutoplay) return;
        const syncAutoplay = () => {
            const s = mobileSwiperRef.current;
            if (!s?.autoplay) return;
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
                s.autoplay.stop();
            } else {
                s.autoplay.start();
            }
        };
        syncAutoplay();
        document.addEventListener('visibilitychange', syncAutoplay);
        return () => document.removeEventListener('visibilitychange', syncAutoplay);
    }, [list.length, shouldAutoplay]);

    if (loading && obras.length === 0) return <ObrasHomeSkeleton />;

    if (list.length === 0) {
        const generoLabel = GENEROS.find((g) => g.formato === formatoId)?.label ?? 'este gênero';
        return (
            <section className="w-full">
                <HomeDesktopFormatoStrip formatoId={formatoId} onFormatoChange={onFormatoChange} />
                <div className="w-full max-w-[1400px] mx-auto aspect-[3/4] max-md:max-h-[70vh] md:h-[400px] flex items-center justify-center rounded-2xl border border-white/10 bg-black/50">
                    <p className="text-white/50 text-sm text-center px-4">
                        Nenhum destaque para {generoLabel}. Use a busca para explorar.
                    </p>
                </div>
                <div className="md:hidden">
                    <ExploreSearchAndFormatos
                        formatoId={formatoId}
                        onFormatoChange={onFormatoChange}
                        navigate={navigate}
                        showCarousel={false}
                    />
                </div>
            </section>
        );
    }

    const shouldLoopDesktop = list.length >= 3;
    const initialSlide = desktopCenterIndex;

    return (
        <section className="w-full">
            <HomeDesktopFormatoStrip formatoId={formatoId} onFormatoChange={onFormatoChange} />

            {/* Mobile — mesmo padrão visual do med-app: hero 4/5, banner||capa, gradientes, hentai, CTA com acento */}
            <div className="md:hidden -mx-4 w-[calc(100%+2rem)] max-w-[100vw]">
                <Swiper
                    onSwiper={(instance) => {
                        mobileSwiperRef.current = instance;
                    }}
                    spaceBetween={0}
                    slidesPerView={1}
                    loop={list.length > 1}
                    autoplay={shouldAutoplay ? { delay: 5000, disableOnInteraction: false } : false}
                    pagination={{ clickable: true }}
                    modules={[Autoplay, Pagination]}
                    className="home-swiper-accent w-full !pb-10 !overflow-visible"
                    style={{ ['--home-swiper-accent' as string]: accent } as CSSProperties}
                >
                    {list.map((obra) => {
                        const coverUrl =
                            obra.id === OBRA_MARCA_CINZA_DESKTOP_ID
                                ? OBRA_MARCA_CINZA_DESKTOP_URL
                                : getObraImageUrl(obra.id, obra.banner || obra.imagem, 520) ||
                                  getObraImageUrl(obra.id, obra.imagem) ||
                                  '/icon-192.png';
                        const title = obra.nome || 'Sem nome';
                        return (
                            <SwiperSlide key={obra.id} className="!overflow-visible">
                                <Link
                                    to={generateWorkLink(obra.id)}
                                    className="relative block w-full aspect-[4/5] min-h-[300px] max-h-[480px] sm:min-h-[320px] sm:max-h-[520px] text-left no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                    style={{ outlineColor: accent }}
                                >
                                    <div className="absolute inset-0 overflow-hidden">
                                        <div
                                            className={`absolute inset-0 bg-cover bg-center bg-no-repeat ${
                                                hentaiBanner ? 'grayscale contrast-[1.08] brightness-[0.88]' : ''
                                            }`}
                                            style={{ backgroundImage: `url(${coverUrl})` }}
                                        />
                                        {hentaiBanner && (
                                            <>
                                                <div
                                                    className="absolute inset-0 pointer-events-none bg-gradient-to-b from-red-950/50 via-red-600/20 to-red-950/55 mix-blend-overlay"
                                                    aria-hidden
                                                />
                                                <div
                                                    className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_85%_55%_at_50%_35%,rgba(220,38,38,0.28),transparent_62%)]"
                                                    aria-hidden
                                                />
                                                <div
                                                    className="absolute inset-0 pointer-events-none opacity-90 mix-blend-soft-light bg-[linear-gradient(125deg,rgba(127,29,29,0.35)_0%,transparent_40%,transparent_60%,rgba(153,27,27,0.3)_100%)]"
                                                    aria-hidden
                                                />
                                            </>
                                        )}
                                        <div
                                            className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent pointer-events-none"
                                            aria-hidden
                                        />
                                        <div
                                            className="absolute bottom-0 left-0 right-0 h-40 rounded-t-2xl border-t border-white/5 bg-black/70 pointer-events-none [mask-image:linear-gradient(to_top,black_55%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_top,black_55%,transparent_100%)]"
                                            aria-hidden
                                        />
                                    </div>
                                    <div className="absolute bottom-0 left-0 right-0 z-[1] min-h-[50%] px-5 pb-7 pt-12 flex flex-col rounded-t-2xl bg-gradient-to-t pointer-events-none from-black via-black/75 to-transparent">
                                        <div className="mt-auto flex flex-col items-center pointer-events-auto w-full max-w-2xl -translate-y-[100px]">
                                            <h2 className="text-xl font-bold text-center leading-tight max-w-2xl mb-3 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                                                {title}
                                            </h2>
                                            <span
                                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white font-semibold text-sm active:scale-[0.98] transition-all hover:brightness-110"
                                                style={{
                                                    backgroundColor: accent,
                                                    boxShadow: `0 10px 32px ${rgbaFromHex(accent, 0.38)}, 0 4px 14px ${rgbaFromHex(accent, 0.22)}`,
                                                }}
                                            >
                                                Ler Agora
                                                <Play size={16} fill="currentColor" className="shrink-0" />
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
            </div>

            {/* Desktop — carrossel em cartões (comportamento anterior) */}
            <div className="hidden md:flex w-full flex-col items-center justify-center relative overflow-hidden bg-black group/section">
                <div className="relative w-full max-w-[1400px] z-10 md:mx-auto">
                    <Swiper
                        key={formatoId}
                        grabCursor
                        centeredSlides={false}
                        slidesPerView={1}
                        slidesPerGroup={1}
                        spaceBetween={0}
                        initialSlide={initialSlide}
                        autoplay={
                            shouldAutoplay
                                ? {
                                      delay: 4000,
                                      disableOnInteraction: false,
                                  }
                                : false
                        }
                        loop={shouldLoopDesktop}
                        onSwiper={(swiper) => {
                            desktopSwiperRef.current = swiper;
                            setSwiperActiveIndex(swiper.realIndex);
                            const loop = list.length >= 3;
                            requestAnimationFrame(() => {
                                swiper.update();
                                const ri = swiper.realIndex;
                                if (typeof swiper.slideToLoop === 'function' && loop) {
                                    swiper.slideToLoop(ri, 0);
                                } else {
                                    swiper.slideTo(ri, 0);
                                }
                            });
                        }}
                        onSlideChange={(swiper) => setSwiperActiveIndex(swiper.realIndex)}
                        modules={[Autoplay, Pagination]}
                        className="obras-home-swiper w-full md:!pb-12"
                        pagination={{
                            clickable: true,
                            dynamicBullets: true,
                        }}
                        breakpoints={{
                            768: {
                                centeredSlides: true,
                                slidesPerView: 'auto',
                                spaceBetween: 30,
                            },
                        }}
                        style={
                            {
                                ['--swiper-theme-color' as string]: accent,
                                ['--home-swiper-accent' as string]: accent,
                            } as CSSProperties
                        }
                    >
                        {list.map((obra, index) => {
                            const isActive = index === activeIndex;
                            const isEunucoActiveCenter =
                                obra.id === OBRA_EUNUCO_DESKTOP_BANNER_ID && isActive;
                            const isMarcaCinza = obra.id === OBRA_MARCA_CINZA_DESKTOP_ID;
                            const isMarcaCinzaActiveCenter = isMarcaCinza && isActive;
                            const eunucoExpanded = isEunucoActiveCenter && eunucoRevealBanner;
                            const defaultCoverSrc =
                                getObraImageUrl(obra.id, obra.banner || obra.imagem, 600) ||
                                getObraImageUrl(obra.id, obra.imagem) ||
                                '/icon-192.png';
                            const slideWide = eunucoExpanded || isMarcaCinzaActiveCenter;
                            const slideClass = [
                                'transition-[width,max-width] duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)]',
                                'md:!h-[320px] lg:!h-[380px] xl:!h-[460px]',
                                slideWide
                                    ? [
                                          'md:!w-[min(88vw,680px)]',
                                          'lg:!w-[min(86vw,760px)]',
                                          'xl:!w-[min(84vw,840px)]',
                                      ].join(' ')
                                    : 'md:!w-[240px] lg:!w-[280px] xl:!w-[340px]',
                            ].join(' ');
                            return (
                                <SwiperSlide key={obra.id} className={slideClass}>
                                    <Link
                                        to={generateWorkLink(obra.id)}
                                        className={[
                                            'relative block h-full w-full overflow-visible transition-all duration-700 group',
                                            'rounded-2xl border-0 p-0 shadow-none outline-none ring-0',
                                            isActive
                                                ? 'md:shadow-none'
                                                : 'opacity-40 blur-[0.5px] scale-90 md:translate-y-0 md:opacity-100 md:blur-0 md:scale-100',
                                        ].join(' ')}
                                        style={{
                                            WebkitTapHighlightColor: 'transparent',
                                        }}
                                    >
                                        <div
                                            className="h-full w-full rounded-2xl p-[2px] transition-[filter,opacity] duration-500 group-hover:brightness-105 md:p-[2.5px]"
                                            style={{
                                                background:
                                                    'linear-gradient(135deg, rgba(0,0,0,0.98) 0%, rgba(32,32,32,0.95) 38%, rgba(10,10,10,0.97) 62%, rgba(0,0,0,1) 100%)',
                                            }}
                                        >
                                        <div className="relative h-full w-full overflow-hidden rounded-[13px] bg-black md:h-full md:rounded-[14px] lg:rounded-[15px] xl:rounded-[17px]">
                                            {isEunucoActiveCenter ? (
                                                <div className="absolute inset-0 h-full w-full">
                                                    <ObraCoverImage
                                                        src={defaultCoverSrc}
                                                        alt={obra.nome ?? ''}
                                                        className={[
                                                            'absolute inset-0 z-[1] h-full w-full object-cover object-center transition-opacity duration-1000 ease-out',
                                                            eunucoRevealBanner
                                                                ? 'pointer-events-none scale-100 opacity-0'
                                                                : 'opacity-100 motion-safe:animate-[eunuco-cover-settle_3s_ease-out_forwards]',
                                                        ].join(' ')}
                                                        draggable={false}
                                                    />
                                                    <div
                                                        className={[
                                                            'absolute inset-0 z-[2] overflow-hidden',
                                                            eunucoRevealBanner
                                                                ? 'motion-safe:animate-[eunuco-bg-reveal-sym_1s_cubic-bezier(0.22,1,0.36,1)_both] motion-reduce:!animate-none motion-reduce:![clip-path:inset(0_0_0_0)]'
                                                                : 'pointer-events-none [clip-path:inset(0_50%_0_50%)]',
                                                        ].join(' ')}
                                                    >
                                                        <ObraCoverImage
                                                            src={OBRA_EUNUCO_DESKTOP_BANNER_URL}
                                                            alt={obra.nome ?? ''}
                                                            className="absolute inset-0 h-full w-full object-cover object-center"
                                                            draggable={false}
                                                        />
                                                    </div>
                                                </div>
                                            ) : isMarcaCinza ? (
                                                <ObraCoverImage
                                                    src={OBRA_MARCA_CINZA_DESKTOP_URL}
                                                    alt={obra.nome ?? ''}
                                                    className="pointer-events-none absolute left-1/2 top-0 z-0 h-full w-[min(88vw,680px)] -translate-x-1/2 object-cover object-center transition-transform duration-1000 group-hover:scale-110 lg:w-[min(86vw,760px)] xl:w-[min(84vw,840px)]"
                                                    draggable={false}
                                                />
                                            ) : (
                                                <ObraCoverImage
                                                    src={defaultCoverSrc}
                                                    alt={obra.nome ?? ''}
                                                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-1000 group-hover:scale-110 md:relative md:inset-auto md:block md:h-full"
                                                    draggable={false}
                                                />
                                            )}
                                            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/30 opacity-90 transition-opacity duration-500 group-hover:opacity-75 md:via-black/20 md:to-transparent md:opacity-80" />

                                            <div
                                                className={[
                                                    'absolute bottom-0 left-0 right-0 z-10 flex flex-col justify-end p-6 transition-all duration-700',
                                                    isActive
                                                        ? 'translate-y-0 opacity-100'
                                                        : 'md:translate-y-0 md:opacity-100 translate-y-4 opacity-0',
                                                ].join(' ')}
                                            >
                                                {(obra.total_capitulos ?? 0) > 0 && (
                                                    <div className="mb-2 flex self-center items-center justify-center gap-1 text-[10px] font-medium uppercase tracking-[0.2em] text-white/45 md:mb-2 md:text-[10px] md:tracking-[0.18em] md:text-white/55 [text-shadow:0_1px_8px_rgba(0,0,0,0.65)]">
                                                        <BookOpen
                                                            size={10}
                                                            className="shrink-0 md:h-3 md:w-3"
                                                            style={{ color: rgbaFromHex(accent, 0.85) }}
                                                            strokeWidth={2}
                                                        />
                                                        <span>{obra.total_capitulos} capítulos</span>
                                                    </div>
                                                )}

                                                <h3 className="mb-3 text-center text-lg font-black leading-tight text-white line-clamp-3 drop-shadow-lg md:text-lg lg:text-xl">
                                                    {obra.nome}
                                                </h3>

                                                <div className="flex items-center justify-center gap-4 md:flex-col md:gap-2">
                                                    <div
                                                        className="
                                                        pointer-events-none inline-flex items-center justify-center gap-1.5
                                                        rounded-full border-0 bg-black/40 px-5 py-2.5
                                                        text-[13px] font-medium tracking-wide text-white/90 backdrop-blur-md
                                                        transition-[background-color,transform] duration-300
                                                        group-hover:bg-black/55 group-hover:scale-[1.01]
                                                    "
                                                    >
                                                        <Play
                                                            className="h-3.5 w-3.5 shrink-0"
                                                            style={{ color: rgbaFromHex(accent, 0.95) }}
                                                            strokeWidth={2}
                                                        />
                                                        <span>Ler agora</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        </div>
                                    </Link>
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                </div>

                <div
                    className="pointer-events-none absolute inset-y-0 left-0 z-30 w-20 bg-gradient-to-r from-[#000000] to-transparent md:w-28"
                    aria-hidden
                />
                <div
                    className="pointer-events-none absolute inset-y-0 right-0 z-30 w-20 bg-gradient-to-l from-[#000000] to-transparent md:w-28"
                    aria-hidden
                />

                <style
                    dangerouslySetInnerHTML={{
                        __html: `
                .obras-home-swiper {
                    --swiper-theme-color: ${accent};
                    background-color: #000;
                }
                .obras-home-swiper .swiper-wrapper {
                    background-color: transparent;
                }
                .obras-home-swiper .swiper-slide {
                    background-color: #000;
                }
                .obras-home-swiper .swiper-pagination-bullet,
                .obras-home-swiper .swiper-pagination-bullet-active,
                .obras-home-swiper .swiper-pagination-bullet-active-main,
                .obras-home-swiper .swiper-pagination-bullet-active-prev,
                .obras-home-swiper .swiper-pagination-bullet-active-next {
                    box-shadow: none !important;
                    filter: none !important;
                }
                @media (min-width: 768px) {
                    .obras-home-swiper {
                        overflow: visible !important;
                        padding-bottom: 3rem !important;
                    }
                    .obras-home-swiper .swiper-pagination {
                        z-index: 40 !important;
                    }
                    .obras-home-swiper .swiper-slide {
                        overflow: visible !important;
                        height: auto !important;
                    }
                    .obras-home-swiper .swiper-wrapper {
                        align-items: stretch;
                    }
                }
                @keyframes eunuco-cover-settle {
                    0% { transform: scale(1.1); }
                    100% { transform: scale(1); }
                }
                @keyframes eunuco-bg-reveal-sym {
                    0% { clip-path: inset(0 50% 0 50%); }
                    100% { clip-path: inset(0 0 0 0); }
                }
            `,
                    }}
                />
            </div>

            <div className="md:hidden">
                <ExploreSearchAndFormatos
                    formatoId={formatoId}
                    onFormatoChange={onFormatoChange}
                    navigate={navigate}
                    showCarousel={list.length > 0}
                />
            </div>
        </section>
    );
}
